import { useState, useMemo } from 'react';
import api from '../utils/api.js';
import { userFacingDetail } from '../utils/errorHandler.js';

/**
 * 活動詳細ページにおける「AI処理（要約・フィードバック生成など）」の
 * 状態管理とAPI通信ロジックをカプセル化するカスタムフック。
 */
export function useAIProcessing({ meetingId, meetingData, setMeetingData, isEditing }) {
  // --- 音声アップロード・AI処理用 State ---
  const [processingStatus, setProcessingStatus] = useState('idle');
  const [processingError, setProcessingError] = useState(null);
  const [audioAttachment, setAudioAttachment] = useState(null);

  // --- 各種モーダル開閉用 State ---
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSummaryConfirmModal, setShowSummaryConfirmModal] = useState(false);
  const [showFeedbackConfirmModal, setShowFeedbackConfirmModal] = useState(false);
  const [showPromptSelectModal, setShowPromptSelectModal] = useState(false);
  
  // プロンプト選択のコンテキスト管理
  const [promptSelectContext, setPromptSelectContext] = useState(null); // 'summary' | 'upload_summary' | 'upload_feedback' | 'feedback'
  const [pendingSummaryPromptId, setPendingSummaryPromptId] = useState(null);

  // 💡 【リファクタ対象②】hasAudioOrTranscript の useMemo 化
  // 複数箇所で実行されていた重いガード条件をメモ化
  const hasAudioOrTranscript = useMemo(() => {
    return !!(audioAttachment || meetingData?.meeting_transcript);
  }, [audioAttachment, meetingData?.meeting_transcript]);

  /**
   * 便利なPOSTリクエストのヘルパー関数
   */
  const postRequest = async (url) => {
    const response = await api.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response) return null;
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(userFacingDetail(response.status, errorData) || '処理に失敗しました。');
    }
    return await response.json();
  };

  // ==========================================
  // ① 音声アップロード後の「AI処理（並列生成）」フロー
  // ==========================================
  const handleConfirmAIProcessing = () => {
    setShowConfirmModal(false);
    setPromptSelectContext('upload_summary');
    setShowPromptSelectModal(true);
  };

  const handleCancelAIProcessing = () => {
    setShowConfirmModal(false);
  };

  const handleAIProcessingWithPrompt = async (summaryPromptId, feedbackPromptId) => {
    setShowPromptSelectModal(false);
    setPromptSelectContext(null);
    setPendingSummaryPromptId(null);
    setShowConfirmModal(false);
    setProcessingError(null);
    let updatedMeetingData = null;

    try {
      setProcessingStatus('transcribing');
      updatedMeetingData = await postRequest(`/generate_transcript/${meetingId}/`);
      if (!updatedMeetingData) return;
      setMeetingData(updatedMeetingData);

      setProcessingStatus('analyzing');
      const params = new URLSearchParams();
      if (summaryPromptId) params.append('summary_prompt_id', summaryPromptId);
      if (feedbackPromptId) params.append('feedback_prompt_id', feedbackPromptId);
      const queryString = params.toString();
      const url = queryString
        ? `/meetings/${meetingId}/generate_ai_analysis?${queryString}`
        : `/meetings/${meetingId}/generate_ai_analysis`;
      updatedMeetingData = await postRequest(url);
      if (!updatedMeetingData) return;
      setMeetingData(updatedMeetingData);

      setProcessingStatus('idle');
    } catch (err) {
      console.error(err);
      setProcessingError(err.message);
      setProcessingStatus('idle');
      if (updatedMeetingData) {
        setMeetingData(updatedMeetingData);
      }
    }
  };

  // ==========================================
  // ② 「AI要約を実行」フロー
  // ==========================================
  const handleClickGenerateSummary = () => {
    if (processingStatus !== 'idle' || !hasAudioOrTranscript || isEditing) return;

    if (meetingData.meeting_summary && meetingData.meeting_summary.trim() !== '') {
      setShowSummaryConfirmModal(true);
    } else {
      setPromptSelectContext('summary');
      setShowPromptSelectModal(true);
    }
  };

  const handleConfirmOverwriteSummary = () => {
    setShowSummaryConfirmModal(false);
    setPromptSelectContext('summary');
    setShowPromptSelectModal(true);
  };

  const handleCancelOverwriteSummary = () => {
    setShowSummaryConfirmModal(false);
  };

  const handleGenerateSummaryOnly = async (promptId = '') => {
    if (processingStatus !== 'idle' || !hasAudioOrTranscript || isEditing) return;
    setProcessingError(null);
    setProcessingStatus('summarizing');

    try {
      const url = promptId
        ? `/generate_summary/${meetingId}/?prompt_id=${promptId}`
        : `/generate_summary/${meetingId}/`;
      const updatedMeetingData = await postRequest(url);
      if (!updatedMeetingData) return;
      setMeetingData(updatedMeetingData);
      setProcessingStatus('idle');
    } catch (err) {
      console.error(err);
      setProcessingError(err.message);
      setProcessingStatus('idle');
    }
  };

  // ==========================================
  // ③ 「AIフィードバックを実行」フロー
  // ==========================================
  const handleClickGenerateFeedback = () => {
    if (processingStatus !== 'idle' || !hasAudioOrTranscript || isEditing) return;

    if (meetingData.ai_feedback && meetingData.ai_feedback.trim() !== '') {
      setShowFeedbackConfirmModal(true);
    } else {
      setPromptSelectContext('feedback');
      setShowPromptSelectModal(true);
    }
  };

  const handleConfirmOverwriteFeedback = () => {
    setShowFeedbackConfirmModal(false);
    setPromptSelectContext('feedback');
    setShowPromptSelectModal(true);
  };

  const handleCancelOverwriteFeedback = () => {
    setShowFeedbackConfirmModal(false);
  };

  const handleGenerateFeedbackOnly = async (promptId = '') => {
    if (processingStatus !== 'idle' || !hasAudioOrTranscript || isEditing) return;
    setProcessingError(null);
    setProcessingStatus('generating_feedback');

    try {
      const url = promptId
        ? `/meetings/${meetingId}/generate_feedback?prompt_id=${promptId}`
        : `/meetings/${meetingId}/generate_feedback`;
      const updatedMeetingData = await postRequest(url);
      if (!updatedMeetingData) return;
      setMeetingData(updatedMeetingData);
      setProcessingStatus('idle');
    } catch (err) {
      console.error(err);
      setProcessingError(err.message);
      setProcessingStatus('idle');
    }
  };

  // ==========================================
  // ④ プロンプト選択モーダル共通アクション
  // ==========================================
  const handlePromptSelectConfirm = (promptId) => {
    setShowPromptSelectModal(false);

    if (promptSelectContext === 'upload_summary') {
      setPendingSummaryPromptId(promptId);
      setPromptSelectContext('upload_feedback');
      setShowPromptSelectModal(true);
    } else if (promptSelectContext === 'upload_feedback') {
      handleAIProcessingWithPrompt(pendingSummaryPromptId, promptId);
    } else if (promptSelectContext === 'summary') {
      handleGenerateSummaryOnly(promptId);
      setPromptSelectContext(null);
    } else if (promptSelectContext === 'feedback') {
      handleGenerateFeedbackOnly(promptId);
      setPromptSelectContext(null);
    }
  };

  const handleClosePromptSelectModal = () => {
    setShowPromptSelectModal(false);
    setPromptSelectContext(null);
    setPendingSummaryPromptId(null);
  };

  return {
    processingStatus,
    processingError, setProcessingError,
    audioAttachment, setAudioAttachment,
    showConfirmModal, setShowConfirmModal,
    showSummaryConfirmModal, setShowSummaryConfirmModal,
    showFeedbackConfirmModal, setShowFeedbackConfirmModal,
    showPromptSelectModal, setShowPromptSelectModal,
    promptSelectContext, setPromptSelectContext,
    hasAudioOrTranscript,
    
    handleConfirmAIProcessing,
    handleCancelAIProcessing,
    handleClickGenerateSummary,
    handleConfirmOverwriteSummary,
    handleCancelOverwriteSummary,
    handleClickGenerateFeedback,
    handleConfirmOverwriteFeedback,
    handleCancelOverwriteFeedback,
    handlePromptSelectConfirm,
    handleClosePromptSelectModal
  };
}
